import { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Stack, Alert, Typography, Box, Chip, Divider,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import {
  previewReplace, replaceCertificate, fileToBase64,
  CertPreview, CertificateRow,
} from "../services/bindCertificates";

interface Props {
  open:    boolean;
  row:     CertificateRow | null;
  onClose: () => void;
  onDone:  (msg: string) => void;   // éxito → refresca la tabla
}

function fmtDate(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("es-AR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

export default function ReplaceCertDialog({ open, row, onClose, onDone }: Props) {
  const [file,     setFile]     = useState<File | null>(null);
  const [password, setPassword] = useState("");
  const [preview,  setPreview]  = useState<CertPreview | null>(null);
  const [busy,     setBusy]     = useState(false);
  const [err,      setErr]      = useState<string | null>(null);

  useEffect(() => {
    if (open) { setFile(null); setPassword(""); setPreview(null); setErr(null); setBusy(false); }
  }, [open]);

  // Si cambia el archivo o el password, invalidar el preview previo.
  useEffect(() => { setPreview(null); }, [file, password]);

  const account = row?.account ?? "";

  async function onValidate() {
    if (!file) return;
    setBusy(true); setErr(null); setPreview(null);
    try {
      const b64 = await fileToBase64(file);
      setPreview(await previewReplace(account, b64, password));
    } catch (e: any) {
      setErr(e.message ?? "falló la validación");
    } finally { setBusy(false); }
  }

  async function onConfirm() {
    if (!file || !preview?.key_matches) return;
    setBusy(true); setErr(null);
    try {
      const b64 = await fileToBase64(file);
      const r = await replaceCertificate(account, b64, password);
      onDone(`Certificado reemplazado · vence ${fmtDate(r.summary.not_after)} · backup ${r.backup_s3_key ?? "—"}`);
    } catch (e: any) {
      setErr(e.message ?? "falló el reemplazo");
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Reemplazar certificado
        <Typography variant="body2" color="text.secondary">
          {row?.secret_name} {row?.cn ? `· ${row.cn}` : ""}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Button variant="outlined" component="label" disabled={busy}>
            {file ? file.name : "Elegir archivo .pfx / .p12"}
            <input
              type="file"
              accept=".pfx,.p12"
              hidden
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </Button>

          <TextField
            label="Contraseña del .pfx"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="off"
            disabled={busy}
          />

          {err && <Alert severity="error">{err}</Alert>}

          {preview && (
            <Box sx={{ border: "1px solid #EEE", borderRadius: 1, p: 1.5, bgcolor: "#FAFAFA" }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>Preview del certificado</Typography>
                {preview.key_matches
                  ? <Chip size="small" color="success" icon={<CheckCircleIcon />} label="Clave coincide" />
                  : <Chip size="small" color="error" icon={<ErrorIcon />} label="Clave NO coincide" />}
              </Stack>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="body2"><b>CN:</b> {preview.cn ?? "—"}</Typography>
              <Typography variant="body2"><b>CUIT:</b> {preview.cuit ?? "—"}</Typography>
              <Typography variant="body2"><b>Emisor:</b> {preview.issuer ?? "—"}</Typography>
              <Typography variant="body2"><b>Serial:</b> {preview.serial ?? "—"}</Typography>
              <Typography variant="body2"><b>Vigencia:</b> {fmtDate(preview.not_before)} → {fmtDate(preview.not_after)}</Typography>
              {!preview.key_matches && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  La clave privada del .pfx no corresponde a este certificado. No se puede reemplazar.
                </Alert>
              )}
            </Box>
          )}

          <Alert severity="info" sx={{ py: 0.5 }}>
            El usuario y la contraseña de la API no cambian (no están en el .pfx) — se preservan.
            La versión anterior queda como rollback.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancelar</Button>
        {!preview
          ? (
            <Button onClick={onValidate} variant="contained" disabled={busy || !file}>
              {busy ? "Validando…" : "Validar"}
            </Button>
          ) : (
            <Button onClick={onConfirm} variant="contained" color="warning"
                    disabled={busy || !preview.key_matches}>
              {busy ? "Reemplazando…" : "Confirmar reemplazo"}
            </Button>
          )}
      </DialogActions>
    </Dialog>
  );
}
